import type { CollectionSummary } from './api-client'
import { updateCollection } from './collection-service'
import { errorText, type BulkOutcome } from './bulk-progress'
import type { CategoryAssignMode } from './category-service'

/**
 * 選取中的集合要套用的批次動作。
 *
 * 分類指派給集合**自己**，不碰成員——書架的分類篩選看的正是集合自己的分類，指派到成員上
 * 等於做了看不到效果。集合的當前分類就在前端已載入的集合清單裡，因此做完集合運算直接整份
 * `PATCH` 回去；不需要作品那邊「先讀再寫」的窗口，集合只有一個寫入入口。
 */
export type CollectionCategoryClient = { update: typeof updateCollection }

export function nextCategoryIds(current: readonly string[], chosen: readonly string[], mode: CategoryAssignMode): string[] {
  const next = new Set(current)
  for (const id of chosen) {
    if (mode === 'add') next.add(id)
    else next.delete(id)
  }
  return [...next]
}

/** 逐個集合送出，遇錯即停並回報做到哪裡；集合數量的量級是個位數到數十。 */
export async function assignCollectionCategories(
  collections: readonly CollectionSummary[], categoryIds: readonly string[], mode: CategoryAssignMode,
  update: typeof updateCollection = updateCollection,
): Promise<BulkOutcome> {
  const wanted = [...new Set(categoryIds)]
  const completedIds: string[] = []
  if (!collections.length || !wanted.length) return { completed: 0, completedIds, reason: '' }
  for (const collection of collections) {
    const next = nextCategoryIds(collection.categoryIds, wanted, mode)
    // 算出來與現況相同就不送：省一次往返，且結果一樣。
    if (next.length === collection.categoryIds.length && next.every(id => collection.categoryIds.includes(id))) {
      completedIds.push(collection.id)
      continue
    }
    try { await update(collection.id, { categoryIds: next }) }
    catch (cause) { return { completed: completedIds.length, completedIds, reason: errorText(cause), cause } }
    completedIds.push(collection.id)
  }
  return { completed: completedIds.length, completedIds, reason: '' }
}

/** 刪除連同成員，逐個集合。已不存在的集合視為已完成，與作品那條規則相同。 */
export async function deleteCollectionsWithMembers(
  collections: readonly CollectionSummary[], remove: (collectionId: string) => Promise<void>,
): Promise<BulkOutcome> {
  const completedIds: string[] = []
  for (const collection of collections) {
    try { await remove(collection.id) }
    catch (cause) { return { completed: completedIds.length, completedIds, reason: errorText(cause), cause } }
    completedIds.push(collection.id)
  }
  return { completed: completedIds.length, completedIds, reason: '' }
}

/** 改名與備份跳過集合時的說明。沒有集合被跳過時是空字串。 */
export function skippedCollectionsNotice(count: number): string {
  return count ? `另有 ${count} 個集合跳過（這個動作只作用在單本作品上）。` : ''
}
